package org.example.project.engineslot

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import kotlinx.browser.window
import org.example.project.dashboard.PopupEngineCard
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

@Composable
fun SlotViewSettingsPopup(
    config: All,
    selectedStandort: String,
    standorte: List<All>,
    onClose: () -> Unit,
    onSave: (All) -> Unit
) {
    val standortName = slotStandortHeadline(standorte, selectedStandort)
    val standortAddress = slotStandortAddress(standorte, selectedStandort)
        .ifBlank { selectedStandort }

    PopupEngineCard(
        windowKey = "slot-view-settings",
        widthPx = 560,
        heightPx = 500,
        minWidthPx = 520,
        minHeightPx = 460,
        onClose = onClose
    ) {
        var intervalState by androidx.compose.runtime.remember(config) {
            androidx.compose.runtime.mutableStateOf(slotIntervalMinutes(config).toString())
        }
        var fromState by androidx.compose.runtime.remember(config) {
            androidx.compose.runtime.mutableStateOf(config.uhrzeitVon)
        }
        var toState by androidx.compose.runtime.remember(config) {
            androidx.compose.runtime.mutableStateOf(config.uhrzeitBis)
        }
        var bookingLeadDaysState by androidx.compose.runtime.remember(config) {
            androidx.compose.runtime.mutableStateOf(slotBookingLeadDays(config).toString())
        }
        var slotCountState by androidx.compose.runtime.remember(config) {
            androidx.compose.runtime.mutableStateOf(slotCount(config).toString())
        }

        Div({
            classes(SlotStyles.glassPanel)
            style {
                width(100.percent)
                height(100.percent)
                padding(20.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(0.px)
                boxSizing("border-box")
            }
        }) {
            Span({ classes(SlotStyles.popupTitleCentered) }) { Text("Einstellungen") }

            Div({ style { height(30.px) } })

            Div({
                style {
                    width(100.percent)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    minWidth(0.px)
                }
            }) {
                SlotInlineLocation(
                    headline = standortName.ifBlank { selectedStandort.ifBlank { "Kein Standort" } },
                    address = standortAddress.ifBlank { "Keine Adresse" }
                )
            }

            Div({ style { height(30.px) } })

            Div({
                classes(SlotStyles.popupBlock)
            }) {
                Div({
                    classes(SlotStyles.popupFieldGrid)
                }) {
                    PopupFieldLabel("Zeitfenster Ab:")
                    PopupFieldLabel("Zeitfenster bis:")

                    PopupField {
                        Input(InputType.Time, attrs = {
                            classes(SlotStyles.popupTimeInput)
                            value(fromState)
                            style {
                                slotInputStyle()
                                property("color-scheme", "dark")
                            }
                            onInput { fromState = it.value }
                        })
                    }
                    PopupField {
                        Input(InputType.Time, attrs = {
                            classes(SlotStyles.popupTimeInput)
                            value(toState)
                            style {
                                slotInputStyle()
                                property("color-scheme", "dark")
                            }
                            onInput { toState = it.value }
                        })
                    }

                    Div({ classes(SlotStyles.popupFieldGridSpacer) })

                    PopupFieldLabel("Slot L\u00E4nge:")
                    PopupFieldLabel("Vorlauf Tage:")

                    PopupField {
                        Input(InputType.Number, attrs = {
                            classes(SlotStyles.popupCompactInput)
                            value(intervalState)
                            style { slotInputStyle() }
                            onInput { intervalState = it.value?.toString().orEmpty() }
                        })
                    }
                    PopupField {
                        Input(InputType.Number, attrs = {
                            classes(SlotStyles.popupCompactInput)
                            value(bookingLeadDaysState)
                            style { slotInputStyle() }
                            onInput { bookingLeadDaysState = it.value?.toString().orEmpty() }
                        })
                    }

                    Div({ classes(SlotStyles.popupFieldGridSpacer) })

                    PopupFieldLabel("Slot Anzahl:")
                    Div({ classes(SlotStyles.popupFieldGridSpacer) })

                    PopupField {
                        Input(InputType.Number, attrs = {
                            classes(SlotStyles.popupCompactInput)
                            value(slotCountState)
                            style { slotInputStyle() }
                            onInput { slotCountState = it.value?.toString().orEmpty() }
                        })
                    }
                    Div({ classes(SlotStyles.popupFieldGridSpacer) })

                    Div({ classes(SlotStyles.popupFieldGridSpacer) })

                    PopupButtonField {
                        LumaticaButton("Schlie\u00DFen", onClick = onClose, variant = LumaticaButtonVariant.Standard)
                    }
                    PopupButtonField {
                        LumaticaButton(
                            "Speichern",
                            onClick = {
                                onSave(
                                    slotConfigUpdated(
                                        config = config,
                                        intervalMinutes = intervalState.toIntOrNull()?.coerceIn(15, 180)
                                            ?: slotIntervalMinutes(config),
                                        von = fromState,
                                        bis = toState,
                                        slotCount = slotCountState.toIntOrNull()?.coerceIn(1, 40)
                                            ?: slotCount(config),
                                        bookingLeadHours = slotBookingLeadHours(config),
                                        bookingLeadDays = bookingLeadDaysState.toIntOrNull()?.coerceIn(0, 365)
                                            ?: slotBookingLeadDays(config)
                                    ).copy(standort = selectedStandort)
                                )
                            },
                            variant = LumaticaButtonVariant.Action
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun PopupFieldLabel(text: String) {
    Div({
        style {
            width(158.px)
            maxWidth(100.percent)
        }
    }) {
        Span({ classes(SlotStyles.popupFieldLabel) }) { Text(text) }
    }
}

@Composable
private fun PopupField(content: @Composable () -> Unit) {
    Div({
        style {
            width(158.px)
            maxWidth(100.percent)
        }
    }) {
        content()
    }
}

@Composable
private fun PopupButtonField(content: @Composable () -> Unit) {
    Div({
        classes(SlotStyles.popupButtonCell)
    }) {
        content()
    }
}

@Composable
fun SlotBookingInfoPopup(
    booking: All,
    masterData: List<All>,
    standorte: List<All>,
    onClose: () -> Unit
) {
    val (popupWidthPx, popupMinWidthPx) = slotBookingPopupWidth(booking)
    PopupEngineCard(
        windowKey = "slot-view-booking-v3-${booking.id}",
        widthPx = popupWidthPx,
        heightPx = null,
        minWidthPx = popupMinWidthPx,
        resizable = true,
        onClose = onClose
    ) {
        SlotBookingReadonlyFormCard(booking, masterData, standorte)
    }
}

fun slotHoverOverlayPosition(booking: All, anchorX: Double, anchorY: Double): Pair<Double, Double> {
    val viewportWidth = window.innerWidth.toDouble()
    val viewportHeight = window.innerHeight.toDouble()
    val overlayWidth = minOf(slotBookingPopupWidth(booking).first.toDouble(), 1320.0)
    val overlayHeight = if (slotBookingArtCode(booking) == SLOT_BEREICH_WE) 720.0 else 560.0
    val gap = 16.0
    val margin = 12.0

    val rightCandidate = anchorX + gap
    val leftCandidate = anchorX - overlayWidth - gap
    val x = when {
        rightCandidate + overlayWidth <= viewportWidth - margin -> rightCandidate
        leftCandidate >= margin -> leftCandidate
        else -> (viewportWidth - overlayWidth - margin).coerceAtLeast(margin)
    }

    val belowCandidate = anchorY + gap
    val aboveCandidate = anchorY - overlayHeight - gap
    val y = when {
        belowCandidate + overlayHeight <= viewportHeight - margin -> belowCandidate
        aboveCandidate >= margin -> aboveCandidate
        else -> (viewportHeight - overlayHeight - margin).coerceAtLeast(margin)
    }

    return x to y
}

@Composable
fun SlotBookingHoverOverlay(
    booking: All,
    masterData: List<All>,
    standorte: List<All>,
    x: Double,
    y: Double,
) {
    Div({
        style {
            position(Position.Fixed)
            left(x.px)
            top(y.px)
            property("z-index", "12050")
            property("pointer-events", "none")
            maxWidth(1320.px)
            maxHeight(86.vh)
            overflow("hidden")
        }
    }) {
        SlotBookingReadonlyFormCard(
            booking = booking,
            masterData = masterData,
            standorte = standorte,
            solidBackground = true
        )
    }
}
